import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { FormattedMessage } from '@futrli/localisation';

import { DeleteDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { ReportSchedulingView } from '@futrli/views/ReportSchedulingView';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { ScheduleListItem } from '@futrli/views/processors/reportScheduling/reportSchedulingList';

import { useRouteIds } from '../../../hooks/useRouteIds';

interface DeleteScheduleDialogContainerProps {
  isOpen: boolean;
  onClose: () => void;
  scheduleItem: ScheduleListItem;
}

export const DeleteScheduleDialogContainer = ({ isOpen, onClose, scheduleItem }: DeleteScheduleDialogContainerProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId, isConsolidation } = useRouteIds();

  const view = useApiView(
    useMemo(
      () =>
        new ReportSchedulingView(workspaceId, organisationId, isConsolidation, {
          schedules: false,
          awaitingReview: false,
          exported: false
        }),
      [workspaceId, organisationId, isConsolidation]
    )
  );

  const { screen, handleAction: handleDelete, handleClose } = useDialogWizardState({
    isLoading: view.state.isDeletingScheduleItem,
    error: view.state.errorDeletingScheduleItem,
    onAction: () => view.deleteScheduleItem(scheduleItem),
    onClose
  });

  const title = intl.formatMessage({ id: 'reporting.delete_schedule', defaultMessage: 'Delete schedule' });

  const deleteDescription = intl.formatMessage({
    id: 'reporting.confirm_delete_schedule',
    defaultMessage: 'Are you sure you want to delete this schedule?'
  });

  const thingsToKnow = [
    <FormattedMessage
      key="unrecoverable"
      id="reporting.delete_schedule_things_to_know_unrecoverable"
      defaultMessage="Deleting a schedule is not recoverable"
    />,
    <FormattedMessage
      key="access"
      id="reporting.delete_schedule_things_to_know_access"
      defaultMessage="All users will immediately lose access to this schedule"
    />
  ];

  const loadingMessage = intl.formatMessage({
    id: 'reporting.deleting_schedule',
    defaultMessage: 'Deleting schedule....'
  });

  const successMessage = intl.formatMessage({
    id: 'reporting.schedule_deleted',
    defaultMessage: 'Schedule deleted'
  });

  const errorMessage = view.state.errorDeletingScheduleItem?.message;

  return (
    <DeleteDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onDelete={handleDelete}
      screen={screen}
      title={title}
      deleteDescription={deleteDescription}
      thingsToKnow={thingsToKnow}
      loadingMessage={loadingMessage}
      successMessage={successMessage}
      errorMessage={errorMessage}
      dataTestId="delete-schedule-dialog"
    />
  );
};
